// Generate QR codes for all tables

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../../config/firebase/config';
import { collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { getMesaPaymentUrl } from '../../../config/domain';
import QRCode from 'qrcode';
import { Download, Copy, QrCode } from 'lucide-react';
import { toast } from 'react-toastify';

interface Mesa {
  id: string;
  numeroMesa: string;
  estado: string;
  ultimaActualizacion?: Timestamp | null;
  posOrderId?: string | null;
}

interface MesaQRData {
  mesa: Mesa;
  qrDataUrl: string;
  isGenerating: boolean;
}

const FILTER_KEY = 'adminMesasFilter';

const QRCodesScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurant, isLoading, error: restaurantError } = useRestaurant();
  const [mesasQRData, setMesasQRData] = useState<MesaQRData[]>([]);
  const [loading, setLoading] = useState(true);

  // Handle QR codes navigation
  const handleQRCodesToggle = () => {
    navigate('/admin/mesas');
  };

  // Handle filter click from QR page
  const handleFilterClick = (value: string) => {
    localStorage.setItem(FILTER_KEY, value);
    navigate('/admin/mesas');
  };

  useEffect(() => {
    if (!restaurant?.id) return;

    const mesasRef = collection(db, 'restaurants', restaurant.id, 'mesas');
    const unsubscribe = onSnapshot(
      mesasRef,
      (snapshot) => {
        try {
          const mesas = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        ultimaActualizacion: (() => {
          const raw = doc.data().ultimaActualizacion;
          if (!raw) return null;
          if (raw instanceof Timestamp) return raw;
          if (raw.seconds && raw.nanoseconds) {
            return new Timestamp(raw.seconds, raw.nanoseconds);
          }
          return null;
        })(),
      } as Mesa));

      // Generate QR codes pointing to payment domain so customers never hit admin
      const generateQRCodes = async () => {
        const qrDataPromises = mesas.map(async (mesa) => {
          try {
            const mesaUrl = getMesaPaymentUrl(mesa.id);
            const qrDataUrl = await QRCode.toDataURL(mesaUrl, {
              width: 200,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            return {
              mesa,
              qrDataUrl,
              isGenerating: false
            };
          } catch (error) {
            console.error('Error generating QR code for mesa:', mesa.numeroMesa, error);
            return {
              mesa,
              qrDataUrl: '',
              isGenerating: false
            };
          }
        });

        const qrData = await Promise.all(qrDataPromises);
        setMesasQRData(qrData);
        setLoading(false);
      };

      generateQRCodes();
        } catch (err) {
          console.error('QRCodesScreen: snapshot callback error', err);
        }
      },
      (err) => {
        console.error('QRCodesScreen: onSnapshot error', err);
      }
    );

    return () => unsubscribe();
  }, [restaurant?.id]);

  const handleDownloadQR = (mesa: Mesa, qrDataUrl: string) => {
    if (!qrDataUrl) {
      toast.error('QR code no disponible');
      return;
    }
    
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `mesa-${mesa.numeroMesa}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`QR de Mesa ${mesa.numeroMesa} descargado`);
  };

  const handleCopyLink = async (mesa: Mesa) => {
    const mesaUrl = getMesaPaymentUrl(mesa.id);
    try {
      await navigator.clipboard.writeText(mesaUrl);
      toast.success(`Enlace de Mesa ${mesa.numeroMesa} copiado al portapapeles`);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Error al copiar el enlace');
    }
  };

  if (isLoading || !restaurant?.id) {
    return <div className="p-4">Cargando restaurante...</div>;
  }

  if (restaurantError) {
    return <div className="p-4 text-red-600">Error: {restaurantError}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b shadow-sm">
        <div className="flex flex-col md:flex-row items-center justify-between max-w-7xl mx-auto px-4 py-4 gap-4">
          <div className="flex items-center gap-4 w-full md:w-auto">
            {restaurant?.logoUrl && (
              <img
                src={restaurant.logoUrl ? `${restaurant.logoUrl}?t=${new Date().getTime()}` : undefined}
                alt={`Logo de ${restaurant.name}`}
                className="h-14 w-14 object-contain rounded bg-white border"
              />
            )}
            <h1 className="text-2xl font-semibold truncate">Códigos QR de las Mesas</h1>
          </div>
        </div>
        {/* Navigation Tabs */}
        <div className="sticky top-[72px] z-10 bg-white border-b">
          <div className="flex items-center justify-between max-w-7xl mx-auto px-4 py-2">
            {/* Left side - Status filters (now enabled) */}
            <div className="flex gap-2">
              {[
                { label: 'Todas', value: 'todas' },
                { label: 'Abiertas', value: 'abierta' },
                { label: 'Cerradas', value: 'cerrada' },
              ].map(tab => (
                <button
                  key={tab.value}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 text-gray-700 hover:bg-gray-200`}
                  onClick={() => handleFilterClick(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Right side - QR Codes button */}
            <div className="flex items-center">
              <div className="w-px h-6 bg-gray-300 mx-4"></div>
              <button
                className="px-4 py-2 rounded-lg font-medium bg-blue-600 text-white shadow transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleQRCodesToggle}
              >
                Códigos QR
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Generando códigos QR...</div>
          </div>
        ) : mesasQRData.length === 0 ? (
          <div className="text-center py-12">
            <QrCode className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay mesas disponibles</h3>
            <p className="text-gray-500">Crea algunas mesas para generar códigos QR.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mesasQRData.map(({ mesa, qrDataUrl, isGenerating }) => (
              <div key={mesa.id} className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center">
                {/* Mesa Number */}
                <h3 className="text-xl font-bold text-gray-800 mb-4">Mesa {mesa.numeroMesa}</h3>
                
                {/* Status Badge */}
                <span className={`px-3 py-1 rounded-full text-xs font-medium mb-4 ${
                  mesa.estado === 'abierta' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {mesa.estado}
                </span>

                {/* QR Code */}
                <div className="flex flex-col items-center mb-4">
                  {isGenerating ? (
                    <div className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-sm text-gray-500">Generando...</div>
                    </div>
                  ) : qrDataUrl ? (
                    <img 
                      src={qrDataUrl} 
                      alt={`QR Code para Mesa ${mesa.numeroMesa}`}
                      className="w-40 h-40 border rounded-lg bg-white shadow-sm"
                    />
                  ) : (
                    <div className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-sm text-gray-500">Error al generar QR</div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 w-full">
                  <button
                    onClick={() => handleDownloadQR(mesa, qrDataUrl)}
                    disabled={!qrDataUrl}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Descargar QR
                  </button>
                  
                  <button
                    onClick={() => handleCopyLink(mesa)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Copiar Enlace
                  </button>
                </div>

                {/* Mesa URL Display (payment domain so customers stay off admin) */}
                <div className="mt-4 w-full">
                  <p className="text-xs text-gray-500 text-center break-all">
                    {getMesaPaymentUrl(mesa.id)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodesScreen; 